import { useEffect, useState } from 'react';

/**
 * Registr rizik BOZP — datová vrstva.
 *
 * Zdroj dat je `rizika.db.json` (1 642 rizik), převzatý 1:1 z původní
 * samostatné aplikace `frontend/rizika.html` (ta vznikla z firemního Excelu
 * „Centrální registr rizik BOZP"). Soubor je velký (~550 kB), proto se načítá
 * líně přes dynamický import — do hlavního bundle se nedostane.
 *
 * Metodika (VÚBP): R = P × N, hodnocení H podle pásma R:
 *   1–5 A akceptovatelná · 6–10 P přijatelná · 11–15 N nežádoucí ·
 *   16–20 VN velmi nežádoucí · 21–25 ! nepřijatelná.
 */

export interface Riziko {
  id: number;
  /** index do `kategorie` */
  kat: number;
  /** index do `cinnosti` */
  cin: number;
  /** index do `zdroje` */
  zdroj: number;
  nebezpeci: string;
  p: number;
  n: number;
  opatreni: string;
  oopp: string;
}

export interface RizikaDb {
  kategorie: string[];
  cinnosti: string[];
  zdroje: string[];
  rizika: Riziko[];
}

export type RiskLevelCode = 'A' | 'P' | 'N' | 'VN' | '!';

export interface RiskLevel {
  r: number;
  code: RiskLevelCode | '';
  label: string;
  /** Slovní hodnocení H (velmi nízká … velmi vysoká). */
  h: string;
  opatreni: string;
}

export const RISK_LEVELS: Array<{
  code: RiskLevelCode;
  from: number;
  to: number;
  label: string;
  h: string;
  opatreni: string;
}> = [
  { code: 'A', from: 1, to: 5, label: 'Akceptovatelná', h: 'velmi nízká', opatreni: '—' },
  { code: 'P', from: 6, to: 10, label: 'Přijatelná', h: 'nízká', opatreni: 'nápravné opatření' },
  { code: 'N', from: 11, to: 15, label: 'Nežádoucí', h: 'střední', opatreni: 'nápravné opatření' },
  { code: 'VN', from: 16, to: 20, label: 'Velmi nežádoucí', h: 'vysoká', opatreni: 'bezpečnostní opatření' },
  { code: '!', from: 21, to: 25, label: 'Nepřijatelná', h: 'velmi vysoká', opatreni: 'zastavení činnosti' },
];

export const PRAVDEPODOBNOST = [
  'Nepravděpodobná',
  'Nahodilá',
  'Pravděpodobná',
  'Velmi pravděpodobná',
  'Trvalá',
];

export const NASLEDKY = [
  'Poranění bez pracovní neschopnosti',
  'Absenční úraz (s pracovní neschopností)',
  'Závažnější úraz vyžadující hospitalizaci',
  'Závažný úraz s trvalými následky',
  'Smrtelný úraz',
];

export function riskLevel(p: number, n: number): RiskLevel {
  const r = p * n;
  if (r <= 0) return { r: 0, code: '', label: '—', h: '—', opatreni: '—' };
  const lvl = RISK_LEVELS.find((l) => r >= l.from && r <= l.to) ?? RISK_LEVELS[RISK_LEVELS.length - 1];
  return { r, code: lvl.code, label: lvl.label, h: lvl.h, opatreni: lvl.opatreni };
}

/** Klíč činnosti v rámci kategorie (stejná činnost se může vyskytnout ve dvou kategoriích). */
export function cinnostKey(kat: number, cin: number): string {
  return `${kat}:${cin}`;
}

export interface CinnostInfo {
  key: string;
  kat: number;
  cin: number;
  nazev: string;
  pocet: number;
}

/** Činnosti seskupené po kategoriích (v pořadí kategorií z DB, činnosti abecedně). */
export function cinnostiPodleKategorii(db: RizikaDb): Array<{ kat: number; nazev: string; cinnosti: CinnostInfo[] }> {
  const counts = new Map<string, CinnostInfo>();
  for (const r of db.rizika) {
    const key = cinnostKey(r.kat, r.cin);
    const c = counts.get(key);
    if (c) c.pocet += 1;
    else counts.set(key, { key, kat: r.kat, cin: r.cin, nazev: db.cinnosti[r.cin], pocet: 1 });
  }
  return db.kategorie.map((nazev, kat) => ({
    kat,
    nazev,
    cinnosti: [...counts.values()]
      .filter((c) => c.kat === kat)
      .sort((a, b) => a.nazev.localeCompare(b.nazev, 'cs')),
  }));
}

let cached: RizikaDb | null = null;
let loading: Promise<RizikaDb> | null = null;

export function loadRizikaDb(): Promise<RizikaDb> {
  if (cached) return Promise.resolve(cached);
  if (!loading) {
    loading = import('./rizika.db.json').then((m) => {
      cached = (m.default ?? m) as RizikaDb;
      return cached;
    });
  }
  return loading;
}

/** Líně načtená databáze rizik (null, dokud se nestáhne). */
export function useRizikaDb(): { db: RizikaDb | null; error: string | null } {
  const [db, setDb] = useState<RizikaDb | null>(cached);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (db) return;
    let alive = true;
    loadRizikaDb()
      .then((d) => alive && setDb(d))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : 'Databázi rizik se nepodařilo načíst.'));
    return () => {
      alive = false;
    };
  }, [db]);
  return { db, error };
}
